import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiFileText } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto px-6 py-12"
    >
      <Link to="/" className="inline-flex items-center gap-2 text-indigo-600 font-bold mb-8 hover:gap-3 transition-all">
        <SafeIcon icon={FiArrowLeft} /> Back to Home
      </Link>

      <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl border border-slate-100">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
            <SafeIcon icon={FiFileText} className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900">Terms and Conditions</h1>
            <p className="text-sm text-slate-400 font-medium">Last Updated: February 10, 2026</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-600 font-medium leading-relaxed">
          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-2xl text-slate-800">
            <strong>Welcome to ConnectGo!</strong> These Terms and Conditions help you understand how to use our game responsibly and what you can expect from us. We've written this in clear language so you know exactly what you're agreeing to.
          </div>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">1. About ConnectGo</h2>
            <p>ConnectGo is a free, web-based game designed to provide an engaging and educational experience. By accessing or playing ConnectGo at <a href="https://ConnectGo.au/" className="text-indigo-600 underline">ConnectGo.au/</a>, you agree to follow these terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">2. Who Can Use ConnectGo</h2>
            <p>ConnectGo is designed for young adults and learners. If you're under 18, we recommend that you review these terms with a parent, carer, or teacher. By using the game, you confirm that you:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Have the ability to understand and agree to these terms</li>
              <li>Will use the game for its intended purpose: learning and enjoyment</li>
              <li>Will treat other users (if applicable) with respect</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">3. How to Use ConnectGo Responsibly</h2>
            <p><strong>You agree to:</strong></p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Play fairly:</strong> Use the game as intended without attempting to cheat or disrupt the experience</li>
              <li><strong>Be respectful:</strong> If the game includes any interactive features, treat others kindly</li>
              <li><strong>Protect your access:</strong> Keep your device secure when using ConnectGo</li>
              <li><strong>Use appropriate devices:</strong> Access ConnectGo only from devices you’re authorised to use</li>
            </ul>
            <p className="mt-4"><strong>You agree NOT to:</strong></p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Attempt to hack, reverse-engineer, or damage the game</li>
              <li>Use automated tools or bots to play the game</li>
              <li>Share inappropriate content through the game (if applicable)</li>
              <li>Use the game for any commercial purpose without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">4. What We Provide</h2>
            <p>ConnectGo is provided <strong>"as is"</strong> for your educational benefit and enjoyment. This means:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>We strive for quality:</strong> We work to make ConnectGo fun, functional, and accessible</li>
              <li><strong>Things happen:</strong> Sometimes technical issues occur, and the game might be temporarily unavailable</li>
              <li><strong>We can update:</strong> We may modify, update, or improve ConnectGo at any time to enhance your experience</li>
              <li><strong>No guarantees:</strong> While we aim for excellence, we cannot guarantee the game will always work perfectly on every device</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">5. Your Content and Gameplay</h2>
            <p>Any gameplay, scores, or interactions you create while using ConnectGo are yours to enjoy. We respect your experience and creativity. Please note that because <strong>no data is collected</strong>, your progress is not stored between sessions unless saved locally on your device by your browser.</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">6. Intellectual Property</h2>
            <p>ConnectGo, including its design, code, graphics, and gameplay mechanics, is protected by intellectual property rights. You're welcome to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Play and enjoy the game</li>
              <li>Share the website link with others</li>
              <li>Talk about your experience with ConnectGo</li>
            </ul>
            <p className="mt-4">Please don't copy, modify, or distribute the game's code or assets without permission.</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">7. External Links</h2>
            <p>ConnectGo may contain links to external websites or resources. We're not responsible for the content or practices of those external sites. When you click on external links, you're leaving ConnectGo, and different terms may apply.</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">8. Limitation of Liability</h2>
            <p>ConnectGo is an educational game provided free of charge. While we want you to have a great experience:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>We're not responsible for any issues that may arise from using the game</li>
              <li>You use ConnectGo at your own discretion</li>
              <li>We encourage responsible use and taking breaks during gameplay</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">9. Changes to These Terms</h2>
            <p>We may update these Terms and Conditions to improve clarity or reflect changes to ConnectGo. When we make changes:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>We'll update the "Last Updated" date at the top of this page</li>
              <li>Continued use of ConnectGo after changes means you accept the updated terms</li>
              <li>We encourage you to review these terms periodically</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">10. Termination</h2>
            <p>We hope you'll enjoy ConnectGo, but we reserve the right to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Temporarily or permanently discontinue the game</li>
              <li>Restrict access if these terms are violated</li>
              <li>Make changes to improve the user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">11. Contact and Questions</h2>
            <p>We value your feedback and questions. If you have concerns about these Terms and Conditions or need clarification, please reach out to: tc@tcchin.com</p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 mb-3">12. General Provisions</h2>
            <p><strong>Entire Agreement:</strong> These terms represent the complete agreement between you and ConnectGo regarding your use of the game.</p>
            <p className="mt-2"><strong>Severability:</strong> If any part of these terms is found to be unenforceable, the remaining provisions will continue to apply.</p>
            <p className="mt-2"><strong>Waiver:</strong> Our failure to enforce any provision doesn't mean we waive the right to enforce it later.</p>
          </section>

          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-6 rounded-r-2xl text-slate-800 italic">
            <strong>Your Wellbeing Matters:</strong> ConnectGo is designed to be an enjoyable learning tool. Remember to take regular breaks, maintain good posture while playing, and balance screen time with other activities. If you experience any discomfort while playing, please stop and rest.
          </div>

          <p className="pt-8 border-t border-slate-100 text-sm italic text-slate-400">
            By using ConnectGo, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default Terms;